import { strings } from "@perceptron/constants";

import type { RecoveryActivity, RecoveryStatus } from "../recovery/contracts";

const copy = strings.trackingInspector.recovery;

export function RecoverySummary({ recovery }: { recovery: RecoveryActivity | undefined }) {
  if (!recovery?.enabled || !recovery.available) return null;
  const counts = new Map<RecoveryStatus, number>();
  let recoveredFrames = 0;
  for (const interval of recovery.intervals.values()) {
    counts.set(interval.status, (counts.get(interval.status) ?? 0) + 1);
    recoveredFrames += interval.recoveredFrames ?? 0;
  }
  const { found, noMatch, failed, pending } = recovery.outcomes;
  return (
    <section aria-label={copy.title} className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-medium">{copy.title}</h4>
        <span className="min-w-0 truncate text-xs text-foreground-secondary" title={recovery.providerLabel}>
          {recovery.providerLabel}
        </span>
      </div>
      <dl className="grid grid-cols-2 divide-x divide-border rounded-md bg-background-alt-2 py-2.5">
        {[
          [copy.withBox, found],
          [copy.withoutBox, noMatch + failed],
        ].map(([label, value]) => (
          <div className="flex flex-col gap-0.5 px-3" key={label}>
            <dt className="text-xs text-foreground-secondary">{label}</dt>
            <dd className="text-xl font-medium tabular-nums" data-recovery-outcome={label}>
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground-secondary">
        {[
          [copy.noMatch, noMatch],
          [copy.failedCalls, failed],
          [copy.pendingCalls, pending],
        ].map(([label, value]) => (
          <div className="flex gap-1" key={label}>
            <dt>{label}:</dt>
            <dd className="tabular-nums text-foreground">{value}</dd>
          </div>
        ))}
      </dl>
      <dl className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-2 text-xs text-foreground-secondary">
        {[
          [copy.requests, `${recovery.requestsUsed}/${recovery.maxRequests}`],
          [copy.active, `${recovery.activeRequests}/${recovery.concurrency}`],
          [copy.recoveredFrames, recoveredFrames],
        ].map(([label, value]) => (
          <div className="flex gap-1" key={label}>
            <dt>{label}:</dt>
            <dd className="tabular-nums text-foreground">{value}</dd>
          </div>
        ))}
      </dl>
      <details className="text-xs text-foreground-secondary">
        <summary className="cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          {copy.outcomeDetails}
        </summary>
        <p className="mt-2">{copy.outcomesHint}</p>
        {counts.size > 0 && (
          <p className="mt-1">{[...counts].map(([status, count]) => `${copy.stages[status]}: ${count}`).join(" · ")}</p>
        )}
      </details>
    </section>
  );
}
